import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

interface Position {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

interface DropdownProps {
  renderButton: (props: { isOpen: boolean; toggle: () => void }) => ReactNode;
  children: (props: { close: () => void }) => ReactNode;
  disabled?: boolean;
}

export const Dropdown = ({
  renderButton,
  children,
  disabled,
}: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const [position, setPosition] = useState<Position>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
  }, [open]);

  // Calculate position after dropdown renders to ensure it stays on screen
  useLayoutEffect(() => {
    if (!open || !buttonRect || !dropdownRef.current) return;

    const dropdown = dropdownRef.current;
    const dropdownHeight = dropdown.offsetHeight;
    const dropdownWidth = dropdown.offsetWidth;
    const padding = 8;

    // Try to position above the button first
    let top: number | undefined;
    let bottom: number | undefined;
    const spaceAbove = buttonRect.top;
    const spaceBelow = window.innerHeight - buttonRect.bottom;

    if (spaceAbove >= dropdownHeight + padding) {
      // Fits above
      bottom = window.innerHeight - buttonRect.top + padding;
    } else if (spaceBelow >= dropdownHeight + padding) {
      // Fits below
      top = buttonRect.bottom + padding;
    } else {
      // Doesn't fit either way, position to maximize visibility
      if (spaceAbove > spaceBelow) {
        top = padding;
      } else {
        bottom = padding;
      }
    }

    // Horizontal positioning - align right edge with button, but keep on screen
    let right = window.innerWidth - buttonRect.right;
    let left: number | undefined;

    if (right + dropdownWidth > window.innerWidth - padding) {
      // Would overflow left side, align to left edge instead
      left = Math.max(padding, buttonRect.left);
      right = undefined as unknown as number;
    }
    if (right < padding) {
      right = padding;
    }

    setPosition({ top, bottom, left, right });
  }, [open, buttonRect]);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedOutsideContainer =
        containerRef.current && !containerRef.current.contains(target);
      const clickedOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(target);

      if (clickedOutsideContainer && clickedOutsideDropdown) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const toggle = () => {
    if (!disabled) setOpen((v) => !v);
  };

  const close = () => setOpen(false);

  const dropdown = open && buttonRect && (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: position.top,
        bottom: position.bottom,
        left: position.left,
        right: position.right,
        zIndex: 9999,
      }}
    >
      {children({ close })}
    </div>
  );

  return (
    <>
      {open && createPortal(dropdown, document.body)}
      <div ref={containerRef}>
        <div ref={buttonRef}>{renderButton({ isOpen: open, toggle })}</div>
      </div>
    </>
  );
};

// DropdownSelector - options/selector logic built on top of Dropdown

export interface DropdownOption<T> {
  value: T;
  label: string;
  badge?: string; // Optional badge like "New"
}

interface DropdownSelectorProps<T> {
  value?: T; // Optional - if not provided, acts as action menu (no selection)
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  renderButton: (props: { isOpen: boolean; toggle: () => void }) => ReactNode;
  disabled?: boolean;
}

export const DropdownSelector = <T,>({
  value,
  options,
  onChange,
  renderButton,
  disabled,
}: DropdownSelectorProps<T>) => {
  return (
    <Dropdown renderButton={renderButton} disabled={disabled}>
      {({ close }) => (
        <div
          style={{
            maxHeight: `calc(100vh - 16px)`,
            overflowY: 'auto',
            background: 'var(--card-bg)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
        >
          {options.map((option, index) => {
            const isSelected = value !== undefined && option.value === value;
            return (
              <button
                key={String(option.value)}
                onClick={() => {
                  onChange(option.value);
                  close();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '12px 24px',
                  border: 'none',
                  borderTop: index > 0 ? '1px solid var(--border)' : 'none',
                  background: isSelected ? 'var(--accent)' : 'transparent',
                  color: isSelected ? 'var(--background)' : 'var(--primary)',
                  fontSize: '1rem',
                  fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'var(--hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {option.label}
                {option.badge && (
                  <span
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#fff',
                      background: 'var(--teal)',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {option.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </Dropdown>
  );
};

export default Dropdown;
